import styled from "styled-components";
import Flex from "../../../../designComponents/Flex";
import Modal from "../../../../designComponents/Modal";
import { RenderReviewImages } from "../../../../designComponents/Table/renderMethods";
import Typography from "../../../../designComponents/Typography";
import { toCapitalCase } from "../../../../utils/conversion";
import { LabelText, SectionTitle, ValueText } from "../styles";
import { ReviewFieldNameType, ReviewReport } from "../types";
import { humanReadableDate } from "../../../../utils/date";

const makeModalTitle = (field: ReviewFieldNameType) => {
  switch (field) {
    case "Images":
      return "Property Images Review";
    case "Amenities":
      return "Amenities Review";
    case "Inventory":
      return "Inventory Review";
    case "Policy":
      return "Policy Review";
    case "RefRoom":
      return "Reference Images Review";
    case "Rules":
      return "Rules Review";

    default:
      return "Reviews";
  }
};

type Props = {
  isVisible: boolean;
  reviews: ReviewReport[];
  onClose: () => void;
  field: ReviewFieldNameType;
  createdAt: string;
};

const ViewReviews = ({
  isVisible,
  reviews,
  onClose,
  field,
  createdAt,
}: Props) => {
  const review = reviews?.find((r) => r.field === field);
  const title = makeModalTitle(field);

  if (!field) {
    return (
      <Modal
        title={title || "Reviews"}
        onClose={onClose}
        isOpen={isVisible}
        halfWidth
      >
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "0px 16px",
            backgroundColor: "#fefff0",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {createdAt && (
            <Typography
              size={"subheading"}
              fontWeight="semibold"
              style={{ textAlign: "right", margin: "10px 0px " }}
            >
              {humanReadableDate(createdAt)}
            </Typography>
          )}
          {reviews?.length > 0 ? (
            reviews?.map((review) => {
              return <IndividualFieldDetails review={review} />;
            })
          ) : (
            <Flex direction="column" padding="50px 10px" alignItems="center">
              <Typography
                style={{ textAlign: "center" }}
                color="colorDanger"
                size={"title"}
              >
                No reviews
              </Typography>
              <Typography style={{ textAlign: "center" }}>
                All fields were accepted by clicking on accept and publish
                without individually adding review.
              </Typography>
            </Flex>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={title || "Reviews"}
      onClose={onClose}
      isOpen={isVisible}
      halfWidth
    >
      <div
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "0px 16px",
          backgroundColor: "#fefff0",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        {createdAt && (
          <Typography
            size={"subheading"}
            fontWeight="semibold"
            style={{ textAlign: "right", margin: "10px 0px " }}
          >
            {humanReadableDate(createdAt)}
          </Typography>
        )}
        {review && <IndividualFieldDetails review={review} />}
      </div>
    </Modal>
  );
};

export default ViewReviews;

const IndividualFieldDetails = ({ review }: { review: ReviewReport }) => {
  return (
    <CardStyled>
      <Flex>
        <SectionTitle>{review.field}</SectionTitle>
        <Typography
          fontWeight="bold"
          color={
            review.status === "REJECTED" ? "status.danger" : "status.success"
          }
        >
          {toCapitalCase(review?.status || "")}
        </Typography>
      </Flex>
      {review.status === "REJECTED" && (
        <>
          <Flex direction="column" gap={0}>
            <LabelText>Description:</LabelText>
            <ValueText>{review.description}</ValueText>
          </Flex>
          <Flex direction="column">
            <LabelText>Images</LabelText>
            <Flex wrap="wrap">
              <RenderReviewImages images={review?.files || []} resize={false} />
            </Flex>
          </Flex>
        </>
      )}
    </CardStyled>
  );
};

const CardStyled = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fefefe;
  border: 1px solid #dbdbdb;
  gap: 20px;
  border-radius: 10px;
  padding: 20px;
  margin: 14px 0px;
  font-family: Arial, sans-serif;
`;
